import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  BellAlertIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  CircleStackIcon,
  ComputerDesktopIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { badgeClass, Field, formatNumber, getApiErrorMessage, inputClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { PreferencesInput, usePreferences, useUpdatePreferences } from '../../hooks/usePreferences';
import { NOTIFICATION_CATEGORIES, NotificationCategoryKey, ROWS_PER_PAGE_OPTIONS } from '../../lib/preferences';
import companySettingsService from '../../services/company-settings.service';
import { gstRateService } from '../../services/gst-rate.service';
import userService from '../../services/user.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';

// What each area tells you about. The checks run every 15 minutes.
const CATEGORY_DETAILS: Record<NotificationCategoryKey, { label: string; description: string }> = {
  inventory: { label: 'Inventory', description: 'A product runs out, or falls to its minimum or reorder level.' },
  finance: { label: 'Finance', description: 'An invoice passes its due date without being paid.' },
  sales: { label: 'Sales', description: 'A sales order passes its expected delivery date without being shipped.' },
  purchase: { label: 'Purchase', description: 'An approved purchase order passes its expected date without being received.' },
  manufacturing: { label: 'Manufacturing', description: 'A work order passes its planned end date without being finished.' },
  system: { label: 'System', description: 'Messages about your account, such as the welcome message.' },
};

const CATEGORY_ORDER: NotificationCategoryKey[] = ['inventory', 'finance', 'sales', 'purchase', 'manufacturing', 'system'];

const START_PAGES = [
  { value: '/dashboard', label: 'Dashboard' },
  { value: '/inventory', label: 'Inventory' },
  { value: '/manufacturing', label: 'Manufacturing' },
  { value: '/purchase', label: 'Purchase' },
  { value: '/sales', label: 'Sales Orders' },
  { value: '/sales-dashboard', label: 'Sales Dashboard' },
  { value: '/invoices', label: 'Invoices' },
  { value: '/analytics', label: 'Analytics' },
  { value: '/mis', label: 'MIS Dashboard' },
  { value: '/reports', label: 'Reports' },
  { value: '/notifications', label: 'Notifications' },
];

const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';

const Toggle: React.FC<{ checked: boolean; onChange: (checked: boolean) => void; label: string; disabled?: boolean }> = ({
  checked,
  onChange,
  label,
  disabled,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      checked ? 'bg-indigo-600' : 'bg-gray-200',
    )}
  >
    <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5' : 'translate-x-0')} />
  </button>
);

const SectionHeader: React.FC<{ icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; title: string; description: string }> = ({
  icon: Icon,
  title,
  description,
}) => (
  <div className="flex items-start gap-3 border-b border-gray-200 px-4 py-3">
    <div className="rounded-lg bg-indigo-50 p-2">
      <Icon className="h-5 w-5 text-indigo-600" />
    </div>
    <div>
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <p className="mt-0.5 text-xs text-gray-500">{description}</p>
    </div>
  </div>
);

const Settings: React.FC = () => {
  const { can, canAny, canOpen } = usePermissions();
  // Alerts about an area go to everyone whose role may see that area
  const receivesAlerts = canAny('inventory.read', 'sales.read', 'purchase.read', 'manufacturing.read', 'gst.read');

  const { data: preferences, isLoading, isError, error, refetch, isPlaceholderData } = usePreferences();
  const updatePreferences = useUpdatePreferences();

  // The status lines on the workspace cards; each loads on its own and is simply left out when it fails
  const { data: company } = useQuery({
    queryKey: ['company-settings', 'overview'],
    queryFn: () => companySettingsService.getOverview(),
    enabled: can('settings.read'),
  });
  const { data: users } = useQuery({ queryKey: ['users', 'summary'], queryFn: () => userService.getSummary(), enabled: can('users.read') });
  const { data: gstRates } = useQuery({ queryKey: ['gstRates', 'summary'], queryFn: () => gstRateService.getSummary(), enabled: can('master_data.read') });

  // Every change is saved as it is made
  const save = (input: PreferencesInput, message: string) => {
    updatePreferences.mutate(input, {
      onSuccess: () => toast.success(message),
      onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The setting could not be saved')),
    });
  };

  const busy = updatePreferences.isPending || (isLoading && !preferences);
  const categories = preferences?.notifications.categories;
  const enabledCount = categories ? NOTIFICATION_CATEGORIES.filter((key) => categories[key]).length : 0;

  const workspace: {
    title: string;
    description: string;
    route: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    status?: string;
    warn?: boolean;
    show: boolean;
  }[] = [
    {
      title: 'Company Settings',
      description: 'Name, address, GST number, bank details and invoice defaults.',
      route: '/company-settings',
      icon: BuildingOfficeIcon,
      status: company ? (company.settings ? company.settings.companyName : 'Not set up yet') : undefined,
      warn: !!company && !company.settings,
      show: canOpen('/company-settings'),
    },
    {
      title: 'Users',
      description: 'Who can sign in, and the state of their accounts.',
      route: '/users',
      icon: UserGroupIcon,
      status: users ? `${formatNumber(users.active)} active of ${formatNumber(users.total)}` : undefined,
      show: canOpen('/users'),
    },
    {
      title: 'Roles',
      description: 'What each role is allowed to see and do.',
      route: '/roles',
      icon: ShieldCheckIcon,
      show: canOpen('/roles'),
    },
    {
      title: 'GST Rates',
      description: 'The GST rates offered on HSN codes, orders and invoices.',
      route: '/master-data/gst-rates',
      icon: ReceiptPercentIcon,
      status: gstRates ? `${formatNumber(gstRates.active)} active${gstRates.defaultRate ? `, default ${gstRates.defaultRate.rate}%` : ''}` : undefined,
      show: canOpen('/master-data/gst-rates'),
    },
    {
      title: 'Master Data',
      description: 'Companies, plants, work centers, units and item definitions.',
      route: '/master-data',
      icon: Squares2X2Icon,
      show: canOpen('/master-data'),
    },
    {
      title: 'Backup',
      description: 'Back up the data and restore from a backup.',
      route: '/backup',
      icon: CircleStackIcon,
      show: canOpen('/backup'),
    },
    {
      title: 'My Profile',
      description: 'Your name, contact details and password.',
      route: '/profile',
      icon: UserCircleIcon,
      show: canOpen('/profile'),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Your own preferences, saved as you change them, and where the rest of the system is configured.
        </p>
      </div>

      {isError ? (
        <div className={cn(cardClass, 'p-10 text-center')}>
          <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'Your preferences could not be loaded.')}</p>
          <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
            Try again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          {/* Notifications */}
          <section className={cardClass}>
            <SectionHeader
              icon={BellAlertIcon}
              title="Notifications"
              description={
                receivesAlerts
                  ? 'Choose what you are told about. The system checks every 15 minutes.'
                  : 'Alerts about stock, invoices and orders go to the people whose role covers those areas.'
              }
            />
            <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Pop-ups</p>
                <p className="text-xs text-gray-500">Show a pop-up on screen the moment a notification arrives.</p>
              </div>
              <Toggle
                label="Pop-ups"
                checked={preferences?.notifications.popups ?? true}
                disabled={busy}
                onChange={(checked) => save({ notifications: { popups: checked } }, checked ? 'Pop-ups switched on' : 'Pop-ups switched off')}
              />
            </div>
            <div className="flex items-center justify-between px-4 pb-1 pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Areas</p>
              <span className={cn(badgeClass, 'bg-gray-50 text-gray-600 ring-gray-500/20')}>
                {enabledCount} of {NOTIFICATION_CATEGORIES.length} on
              </span>
            </div>
            <ul className="divide-y divide-gray-100">
              {CATEGORY_ORDER.map((key) => (
                <li key={key} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{CATEGORY_DETAILS[key].label}</p>
                    <p className="text-xs text-gray-500">{CATEGORY_DETAILS[key].description}</p>
                  </div>
                  <Toggle
                    label={`${CATEGORY_DETAILS[key].label} notifications`}
                    checked={categories?.[key] ?? true}
                    disabled={busy}
                    onChange={(checked) =>
                      save(
                        { notifications: { categories: { [key]: checked } } },
                        `${CATEGORY_DETAILS[key].label} notifications switched ${checked ? 'on' : 'off'}`,
                      )
                    }
                  />
                </li>
              ))}
            </ul>
            <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              Switching an area off stops new notifications for it. Ones you already have stay until you delete them.{' '}
              <Link to="/notifications" className="font-medium text-indigo-600 hover:text-indigo-500">
                Open notifications
              </Link>
            </p>
          </section>

          <div className="space-y-6">
            {/* Display */}
            <section className={cardClass}>
              <SectionHeader icon={ComputerDesktopIcon} title="Display" description="How the application opens and how much a list shows." />
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <Field label="Start Page" htmlFor="pref-start-page" hint="The page you land on after signing in">
                  <select
                    id="pref-start-page"
                    value={preferences?.startPage ?? '/dashboard'}
                    disabled={busy}
                    onChange={(event) =>
                      save(
                        { startPage: event.target.value },
                        `You will start on ${START_PAGES.find((page) => page.value === event.target.value)?.label}`,
                      )
                    }
                    className={inputClass}
                  >
                    {START_PAGES.map((page) => (
                      <option key={page.value} value={page.value}>
                        {page.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Rows Per Page" htmlFor="pref-rows" hint="What lists start with; each list can still be changed">
                  <select
                    id="pref-rows"
                    value={preferences?.rowsPerPage ?? 10}
                    disabled={busy}
                    onChange={(event) => save({ rowsPerPage: Number(event.target.value) }, `Lists will show ${event.target.value} rows`)}
                    className={inputClass}
                  >
                    {ROWS_PER_PAGE_OPTIONS.map((rows) => (
                      <option key={rows} value={rows}>
                        {rows} rows
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {isPlaceholderData && <p className="px-4 pb-3 text-xs text-gray-400">Checking for your latest preferences…</p>}
            </section>

            {/* The rest of the configuration */}
            <section className={cardClass}>
              <SectionHeader icon={Squares2X2Icon} title="Workspace" description="Where everything else is configured." />
              <ul className="divide-y divide-gray-100">
                {workspace
                  .filter((entry) => entry.show)
                  .map((entry) => (
                    <li key={entry.route}>
                      <Link to={entry.route} className="group flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                        <entry.icon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-indigo-600" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 group-hover:text-indigo-700">{entry.title}</p>
                          <p className="truncate text-xs text-gray-500">{entry.description}</p>
                        </div>
                        {entry.status && (
                          <span
                            className={cn(
                              badgeClass,
                              'hidden max-w-[12rem] truncate sm:inline-flex',
                              entry.warn ? 'bg-amber-50 text-amber-700 ring-amber-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20',
                            )}
                          >
                            {entry.status}
                          </span>
                        )}
                        <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300 group-hover:text-indigo-600" />
                      </Link>
                    </li>
                  ))}
              </ul>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
